/**
 * How an invoice may be settled - the vocabulary behind Settings > Payments.
 *
 * One list, four readers, which is why it sits in `@wa/core` rather than beside
 * any one of them:
 *   - the API blanks the seller's bank details on a document when the transfer
 *     is switched off, and refuses a card checkout when the card is;
 *   - the CRM draws one row per entry on the Payments settings tab;
 *   - the CRM's payment dialog offers the enabled ones as a picker;
 *   - the public document page and the portal decide what to show a client.
 *
 * ── TWO KINDS OF ROW, AND THE DIFFERENCE IS NOT COSMETIC ────────────────────
 *
 * A BUILT-IN is a method the product can actually RENDER. There are exactly two
 * and there is no way to add a third from a settings screen: bank transfer is
 * the IBAN block plus the SEPA code, card is the Stripe checkout button. Each
 * one is a switch over code that already exists.
 *
 * A CUSTOM row is a LABEL and nothing more. "Cash", "Card terminal", "Wise" -
 * these are names an operator puts on a payment that arrived outside the
 * product, so the booking dialog can offer them instead of asking somebody to
 * retype the same word every month. A custom row draws nothing for a client and
 * gates nothing, and the panel copy has to say so, because a switch that looks
 * exactly like the two above it will otherwise be read as one.
 *
 * Anybody adding a client-facing method later adds it HERE, as a built-in, with
 * the rendering behind it - not as a custom row with a hopeful name.
 */

/** The methods the product renders for a client. Order is the display order. */
export const BUILTIN_PAYMENT_METHODS = ["bankTransfer", "card"] as const;

export type BuiltinPaymentMethod = (typeof BUILTIN_PAYMENT_METHODS)[number];

const BUILTIN_SET: ReadonlySet<string> = new Set<string>(BUILTIN_PAYMENT_METHODS);

export function isBuiltinPaymentMethod(value: unknown): value is BuiltinPaymentMethod {
  return typeof value === "string" && BUILTIN_SET.has(value);
}

/**
 * What a built-in WRITES into `Payment.method`.
 *
 * These two strings are history, not naming. They are byte for byte what the
 * existing writers already store - the CRM payment dialog has always prefilled
 * the transfer one, and the card webhook records the Stripe one - and the
 * payments screen filters on the exact stored string. Editing either value
 * would split one method into two rows of that dropdown and orphan every row
 * already in the table. Values are not translated for the same reason: an
 * operator switching interface language must not create a second method.
 *
 * A custom row has no entry here: its label IS its token. See `methodToken`.
 */
export const PAYMENT_METHOD_TOKEN: Record<BuiltinPaymentMethod, string> = {
  bankTransfer: "pangaülekanne",
  card: "stripe",
};

/**
 * What the PRODUCT writes when nobody chose a method at all.
 *
 * "Mark as paid" records the remaining balance without asking how the money
 * arrived, so the row it writes has no method an operator picked. It has always
 * stored the literal `manual`, and that value is history for exactly the reason
 * the two above it are: the payments filter groups on the stored string, so
 * changing it would split one column of the ledger into two.
 *
 * It is NOT offerable and never appears in Settings > Payments: it is not a way
 * to be paid, it is the absence of one. It is named here so that the screens
 * showing it can print a translated label instead of a slug - which is the
 * whole difference between a ledger row that reads "Marked as paid" and one
 * that reads "manual" in every interface language.
 */
export const SYSTEM_PAYMENT_TOKENS = ["manual"] as const;

/** What "Mark as paid" stores. Named so the writer and the readers agree. */
export const MANUAL_PAYMENT_TOKEN = "manual";

export type SystemPaymentToken = (typeof SYSTEM_PAYMENT_TOKENS)[number];

const SYSTEM_SET: ReadonlySet<string> = new Set<string>(SYSTEM_PAYMENT_TOKENS);

/** The stored token back to a system one, or null. Case-folded, like the built-ins. */
export function systemForToken(token: string): SystemPaymentToken | null {
  const folded = token.trim().toLowerCase();
  return SYSTEM_SET.has(folded) ? (folded as SystemPaymentToken) : null;
}

/** The stored token back to the built-in that writes it, or null. Case-folded. */
export function builtinForToken(token: string): BuiltinPaymentMethod | null {
  const folded = token.trim().toLowerCase();
  for (const m of BUILTIN_PAYMENT_METHODS) {
    if (PAYMENT_METHOD_TOKEN[m].toLowerCase() === folded) return m;
  }
  return null;
}

/** How many rows of their own an operator may add. A picker, not a taxonomy. */
export const MAX_CUSTOM_PAYMENT_METHODS = 12;

/** Longest a custom label may be. It has to fit a dropdown and a table cell. */
export const MAX_PAYMENT_LABEL_LENGTH = 40;

/** One row of the Payments settings list. */
export interface PaymentMethodRow {
  /**
   * Identity within the list. A built-in key for the two built-ins, and
   * `custom:<folded label>` for everything else.
   *
   * Derived rather than minted, and that is what makes renaming honest: a
   * custom row IS its label, so renaming one is removing a row and adding
   * another. Payments already booked under the old name keep it, exactly as
   * they do today with the free-text field this list replaces.
   */
  id: string;
  /** The operator's own name. `null` for a built-in, whose label is translated. */
  label: string | null;
  enabled: boolean;
}

/** The whole `crm.paymentMethods` setting. */
export interface CrmPaymentMethods {
  /**
   * Ordered as the operator arranged them. Both built-ins are always present,
   * exactly once each, and the first ENABLED row is what the payment dialog
   * prefills - which is the only thing the order decides.
   */
  rows: PaymentMethodRow[];
}

/**
 * What an installation that has never opened the tab gets.
 *
 * Both built-ins on, which is exactly today's behaviour: every invoice prints
 * its bank details and the portal shows a pay button wherever Stripe is
 * configured. Shipping this feature must change nothing until somebody flips a
 * switch.
 */
export function defaultPaymentMethods(): CrmPaymentMethods {
  return {
    rows: BUILTIN_PAYMENT_METHODS.map((id) => ({ id, label: null, enabled: true })),
  };
}

/** The id a custom row gets from its label. Folded, so "Cash" and "cash" collide. */
export function customPaymentMethodId(label: string): string {
  return `custom:${label.trim().toLowerCase()}`;
}

/**
 * Would this custom label write the same thing the product already writes?
 *
 * The ids cannot answer that on their own: a custom row is keyed `custom:<...>`
 * and a built-in by its key, so the two never collide as identifiers even when
 * their TOKENS are the same string. And the token is what lands in
 * `Payment.method`, which is what the payments filter groups by.
 *
 * The failure this prevents: an operator switches the transfer built-in off but
 * still books transfers by hand, so they add a row named after it. The dialog
 * then offers two entries that differ only in case, the payments split between
 * them, and the filter shows two rows with the same label of which each returns
 * half the money.
 *
 * The system tokens are checked as well as the built-ins, and they are the
 * likelier collision of the two: nobody types "pangaülekanne" into a custom
 * row by accident, but "Manual" is an obvious name for a payment somebody
 * entered by hand - and it is exactly what "Mark as paid" already stores.
 */
export function collidesWithReservedToken(label: string): boolean {
  return builtinForToken(label) !== null || systemForToken(label) !== null;
}

/** Is this row one an operator typed in, rather than one of the two built-ins? */
export function isCustomPaymentRow(row: PaymentMethodRow): boolean {
  return !isBuiltinPaymentMethod(row.id);
}

/**
 * What this row writes into `Payment.method`.
 *
 * A built-in writes its historical token; a custom row writes its own label,
 * trimmed. That keeps the payments filter readable without a translation table:
 * an operator who typed a name sees that name in the dropdown, the same way an
 * imported Perfex method name already reads as itself.
 */
export function methodToken(row: PaymentMethodRow): string {
  return isBuiltinPaymentMethod(row.id) ? PAYMENT_METHOD_TOKEN[row.id] : (row.label ?? "").trim();
}

/**
 * A stored value, cleaned into something the readers can trust.
 *
 * FAILS OPEN, and that is the decision this function exists to make. A row that
 * is missing, a non-object, or a `rows` field that is not an array all come back
 * as the default - both built-ins on. The alternative is an invoice that offers
 * a client no way at all to pay it, sent by a CRM that logged nothing, and
 * discovered by a telephone call. A garbled settings row must not be able to
 * cost somebody an invoice.
 *
 * A well-formed list is honoured as written, INCLUDING one with everything
 * switched off: that is a real decision an operator can make on purpose, and it
 * is not the same event as a corrupt row.
 *
 * Built-ins missing from an otherwise valid list are appended DISABLED, not
 * enabled. Today the panel always sends both, so this only fires for a built-in
 * added to the catalogue after the row was written - and a method nobody has
 * ever seen must not switch itself on across every invoice.
 */
export function parsePaymentMethods(raw: unknown): CrmPaymentMethods {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return defaultPaymentMethods();
  const rawRows = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(rawRows)) return defaultPaymentMethods();

  const rows: PaymentMethodRow[] = [];
  const seen = new Set<string>();
  let customCount = 0;

  for (const entry of rawRows) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const source = entry as { id?: unknown; label?: unknown; enabled?: unknown };
    const enabled = source.enabled === true;

    if (isBuiltinPaymentMethod(source.id)) {
      if (seen.has(source.id)) continue;
      seen.add(source.id);
      rows.push({ id: source.id, label: null, enabled });
      continue;
    }

    // Everything else is a custom row, and a custom row is its label. An empty
    // one is dropped rather than kept as a blank line: it could never be picked
    // in the dialog and would write an empty string into `Payment.method`.
    const label = typeof source.label === "string" ? source.label.trim().slice(0, MAX_PAYMENT_LABEL_LENGTH) : "";
    if (label === "") continue;
    // A name that writes what the product already writes is dropped rather than
    // kept beside it - see `collidesWithReservedToken`. Dropped and not folded
    // INTO the built-in: the operator asked for a second row, and quietly
    // turning it into a switch on the first would be a different answer than
    // they gave.
    if (collidesWithReservedToken(label)) continue;
    if (customCount >= MAX_CUSTOM_PAYMENT_METHODS) continue;
    const id = customPaymentMethodId(label);
    if (seen.has(id)) continue;
    seen.add(id);
    customCount += 1;
    rows.push({ id, label, enabled });
  }

  for (const id of BUILTIN_PAYMENT_METHODS) {
    if (!seen.has(id)) rows.push({ id, label: null, enabled: false });
  }

  return { rows };
}

/** Is that built-in switched on? */
export function paymentMethodOn(settings: CrmPaymentMethods, method: BuiltinPaymentMethod): boolean {
  return settings.rows.some((row) => row.id === method && row.enabled);
}

/** The rows an operator switched on, in their order. */
export function enabledPaymentRows(settings: CrmPaymentMethods): PaymentMethodRow[] {
  return settings.rows.filter((row) => row.enabled);
}

/**
 * What the payment dialog should prefill.
 *
 * The first enabled row, which is the only thing the list's order decides. Null
 * when nothing at all is on - the dialog then falls back to a free-text field,
 * because refusing to record money that has arrived is never the right answer.
 */
export function defaultPaymentToken(settings: CrmPaymentMethods): string | null {
  const first = enabledPaymentRows(settings)[0];
  return first ? methodToken(first) : null;
}
